import * as proto from 'src/proto/compiled-protos';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { BackendService } from '../../services/backend.service';
import { FileSelectorComponent, FileSelectorOutput } from 'src/app/general/components/file-selector/file-selector.component';
import { ToastService } from 'src/app/general/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { NoImagePlaceholderComponent } from 'src/app/general/components/no-image-placeholder/no-image-placeholder.component';
import { DeviceService } from 'src/app/general/services/device.service';
import { NavigationService } from '../../services/navigation.service';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { DialogService } from 'src/app/general/services/dialog.service';
import { Util } from 'src/app/general/util/util';
import { ComponentUtil } from 'src/app/general/util/component-util';
import { BusinessToolbarModel } from '../../model/business-toolbar-model';
import { SessionService } from '../../services/session.service';
import { ToolbarComponent } from 'src/app/general/components/toolbar/toolbar.component';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { ErrorResult, LoadingMessage } from 'src/app/general/util/result';
import { BusinessHeaderComponent } from '../business-header/business-header.component';
import { ActionComponent } from 'src/app/general/components/action/action.component';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// <app-business [businessId]="businessId" *ngIf="businessId"></app-business>

// <app-business [business]="business" *ngIf="business"></app-business>
// ----------------------------------------------------------------------------

@Component({
  selector: 'app-business',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    TranslateModule,
    FileSelectorComponent,
    NoImagePlaceholderComponent,
    LoadableContentComponent,
    BusinessHeaderComponent,
    ToolbarComponent,
    ActionComponent
  ],
  templateUrl: './business.component.html',
  styleUrls: ['./business.component.css']
})
export class BusinessComponent implements OnInit, OnChanges {
  @Input() businessId?: string;
  @Input() businessInput?: proto.waiternow.common.IBusinessProto;

  business: proto.waiternow.common.IBusinessProto;

  businessLoadingController: LoadingController;

  businessToolbarModel: BusinessToolbarModel;

  logoUrl: string | null | undefined;
  homeHeaderForLandscapeScreenUrl: string | null | undefined;
  homeHeaderForPortraitScreenUrl: string | null | undefined;
  menuHeaderForLandscapeScreenUrl: string | null | undefined;
  menuHeaderForPortraitScreenUrl: string | null | undefined;

  constructor(
      public deviceService: DeviceService,
      private backendService: BackendService,
      private sessionService: SessionService,
      private toastService: ToastService,
      private translateService: TranslateService,
      private navigationService: NavigationService,
      private dialogService: DialogService) {
    this.business = new proto.waiternow.common.BusinessProto();
    this.businessToolbarModel = new BusinessToolbarModel(
      this.sessionService,
      this.backendService,
      this.navigationService,
      this.translateService,
      this.dialogService,
      this.toastService,
      /* compactForMobile= */ false,
      /* onLocationDeleted= */ businessId => this.navigationService.goToHomePage()
    );
    this.businessLoadingController = new LoadingController();
  }

  ngOnInit(): void {
    if (this.businessInput) {
      this.business = this.businessInput;
      this.businessLoaded();
    } else {
      this.loadBusiness();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (ComponentUtil.bindingChanged('businessId', changes)) {
      this.ngOnInit();
    }
    if (ComponentUtil.bindingChanged('businessInput', changes)) {
      this.ngOnInit();
    }
  }

  public loadBusiness(): void {
    if (!this.businessId) {
      return;
    }
    this.businessLoadingController.onLoadingStarted(LoadingMessage.withMessageTranslateId('loading_business'));
    this.backendService.getBusiness(
      this.businessId,
      /* onSuccess= */ business => {
        if (business) {
          this.business = business;
          this.businessLoaded();
        }
        this.businessLoadingController.onSuccess();
      },
      /* onError= */ error => {
        this.businessLoadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_fetching_business'));
      }
    );
  }

  private businessLoaded(): void {
    // Adding timestamp to force the browser to load the new image when it is updated in the server, instead of using the cached one.
    // The date is modified so the browser doesn't think all images are the same since they have the same UUID (the business id is
    //  used as the image id for all business images).
    this.logoUrl = this.business.logo?.imageUrl + "?timestamp=" + new Date().getTime();
    this.homeHeaderForLandscapeScreenUrl = this.business.homeHeaderForLandscapeScreen?.imageUrl  + "?timestamp=" + (new Date().getTime() + 1);
    this.homeHeaderForPortraitScreenUrl = this.business.homeHeaderForPortraitScreen?.imageUrl + "?timestamp=" + (new Date().getTime() + 2);
    this.menuHeaderForLandscapeScreenUrl = this.business.menuHeaderForLandscapeScreen?.imageUrl + "?timestamp=" + (new Date().getTime() + 3);
    this.menuHeaderForPortraitScreenUrl = this.business.menuHeaderForPortraitScreen?.imageUrl + "?timestamp=" + (new Date().getTime() + 4);
  }

  public updateBusinessLogo(fileSelectorOutput: FileSelectorOutput<any>): void {
    if (fileSelectorOutput.file) {
      const closeProgressDialog = this.dialogService.openProgressDialog();
      this.backendService.updateBusinessLogo(
        Util.safeString(this.business.id),
        fileSelectorOutput.file,
        /* onSuccess= */ () => {
            closeProgressDialog();
            // The logo image id is the business id, so no need to refresh the business, we just need to
            // refresh the image view. TODO: However, the first time an image is set we will need to refresh the page
            // to fetch the business again, because business.logo will be empty.
            this.logoUrl = this.business.logo?.imageUrl + "?timestamp=" + new Date().getTime();
        },
        /* onError */ error => {
          closeProgressDialog();
          this.translateService.get('error_internal').subscribe(this.toastService.error);
        }
      );
    }
  }

  public updateBusinessHomeHeaderForLandscapeScreen(fileSelectorOutput: FileSelectorOutput<any>): void {
    if (fileSelectorOutput.file) {
      const closeProgressDialog = this.dialogService.openProgressDialog();
      this.backendService.updateBusinessHomeHeaderForLandscapeScreen(
        Util.safeString(this.business.id),
        fileSelectorOutput.file,
        /* onSuccess= */ () => {
            closeProgressDialog();
            // The header image id is the business id, so no need to refresh the business, we just need to
            // refresh the image view. TODO: However, the first time an image is set we will need to refresh the page
            // to fetch the business again, because business.homeHeaderForLandscapeScreen will be empty.
            this.homeHeaderForLandscapeScreenUrl = this.business.homeHeaderForLandscapeScreen?.imageUrl + "?timestamp=" + new Date().getTime();
        },
        /* onError */ error => {
          closeProgressDialog();
          this.translateService.get('error_internal').subscribe(this.toastService.error);
        }
      );
    }
  }

  public updateBusinessHomeHeaderForPortraitScreen(fileSelectorOutput: FileSelectorOutput<any>): void {
    if (fileSelectorOutput.file) {
      const closeProgressDialog = this.dialogService.openProgressDialog();
      this.backendService.updateBusinessHomeHeaderForPortraitScreen(
        Util.safeString(this.business.id),
        fileSelectorOutput.file,
        /* onSuccess= */ () => {
            closeProgressDialog();
            // The header image id is the business id, so no need to refresh the business, we just need to
            // refresh the image view. TODO: However, the first time an image is set we will need to refresh the page
            // to fetch the business again, because business.homeHeaderForPortraitScreen will be empty.
            this.homeHeaderForPortraitScreenUrl = this.business.homeHeaderForPortraitScreen?.imageUrl + "?timestamp=" + new Date().getTime();
        },
        /* onError */ error => {
          closeProgressDialog();
          this.translateService.get('error_internal').subscribe(this.toastService.error);
        }
      );
    }
  }

  public updateBusinessMenuHeaderForLandscapeScreen(fileSelectorOutput: FileSelectorOutput<any>): void {
    if (fileSelectorOutput.file) {
      const closeProgressDialog = this.dialogService.openProgressDialog();
      this.backendService.updateBusinessMenuHeaderForLandscapeScreen(
        Util.safeString(this.business.id),
        fileSelectorOutput.file,
        /* onSuccess= */ () => {
            closeProgressDialog();
            // The header image id is the business id, so no need to refresh the business, we just need to
            // refresh the image view. TODO: However, the first time an image is set we will need to refresh the page
            // to fetch the business again, because business.menuHeaderForLandscapeScreen will be empty.
            this.menuHeaderForLandscapeScreenUrl = this.business.menuHeaderForLandscapeScreen?.imageUrl + "?timestamp=" + new Date().getTime();
        },
        /* onError */ error => {
          closeProgressDialog();
          this.translateService.get('error_internal').subscribe(this.toastService.error);
        }
      );
    }
  }

  public updateBusinessMenuHeaderForPortraitScreen(fileSelectorOutput: FileSelectorOutput<any>): void {
    if (fileSelectorOutput.file) {
      const closeProgressDialog = this.dialogService.openProgressDialog();
      this.backendService.updateBusinessMenuHeaderForPortraitScreen(
        Util.safeString(this.business.id),
        fileSelectorOutput.file,
        /* onSuccess= */ () => {
            closeProgressDialog();
            // The header image id is the business id, so no need to refresh the business, we just need to
            // refresh the image view. TODO: However, the first time an image is set we will need to refresh the page
            // to fetch the business again, because business.menuHeaderForPortraitScreen will be empty.
            this.menuHeaderForPortraitScreenUrl = this.business.menuHeaderForPortraitScreen?.imageUrl + "?timestamp=" + new Date().getTime();
        },
        /* onError */ error => {
          closeProgressDialog();
          this.translateService.get('error_internal').subscribe(this.toastService.error);
        }
      );
    }
  }

  public deleteBusinessLogo(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_delete_business_logo').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.deleteBusinessLogo(
            Util.safeString(this.business.id),
            /* onSuccess= */ () => {
              this.logoUrl = undefined;
              closeProgressDialog();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_internal').subscribe(text => this.toastService.error(text));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }

  public deleteBusinessHomeHeaderForLandscapeScreen(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_delete_header_home_landscape').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.deleteBusinessHomeHeaderForLandscapeScreen(
            Util.safeString(this.business.id),
            /* onSuccess= */ () => {
              this.homeHeaderForLandscapeScreenUrl = undefined;
              closeProgressDialog();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_internal').subscribe(text => this.toastService.error(text));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }

  public deleteBusinessHomeHeaderForPortraitScreen(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_delete_header_home_portrait').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.deleteBusinessHomeHeaderForPortraitScreen(
            Util.safeString(this.business.id),
            /* onSuccess= */ () => {
              this.homeHeaderForPortraitScreenUrl = undefined;
              closeProgressDialog();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_internal').subscribe(text => this.toastService.error(text));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }

  public deleteBusinessMenuHeaderForLandscapeScreen(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_delete_header_menu_landscape').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.deleteBusinessMenuHeaderForLandscapeScreen(
            Util.safeString(this.business.id),
            /* onSuccess= */ () => {
              this.menuHeaderForLandscapeScreenUrl = undefined;
              closeProgressDialog();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_internal').subscribe(text => this.toastService.error(text));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }

  public deleteBusinessMenuHeaderForPortraitScreen(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_delete_header_menu_portrait').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.deleteBusinessMenuHeaderForPortraitScreen(
            Util.safeString(this.business.id),
            /* onSuccess= */ () => {
              this.menuHeaderForPortraitScreenUrl = undefined;
              closeProgressDialog();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_internal').subscribe(text => this.toastService.error(text));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }
}
